// SCR-STAFF-891 — داشبورد کنترل روزانه (US-ACC-080): مقایسه جمع کنترلی ماژول‌ها با ثبت‌ها (دریافت‌ها، فروش، سند = منبع، صف خطا، مانده کیف پول
// و بسته) به ازای روز و شعبه با اختلاف برجسته، اجرای دستی کنترل یک روز (AC-ACC-080.1).
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { unwrap, type AccDailyControlView } from '@phoenix/api-client';
import { useAccess, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  DataTable,
  ErrorState,
  faDigits,
  JalaliDate,
  JalaliDatePickerRHF,
  ListPage,
  toBusinessDate,
  type DataTableColumn,
} from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { accKeys, ControlChip, num, Rial, SCR_STAFF_891, useAccBranches, useBranchName } from './common';

function daysAgo(days: number): string {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return toBusinessDate(date) ?? '';
}

export function DailyControlPage() {
  const { t } = useTranslation('acc');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const queryClient = useQueryClient();
  const branches = useAccBranches();
  const branchName = useBranchName();
  const canRun = access.accessMode === 'Full' && access.can('acc.posting_errors.manage');
  const { control, watch } = useForm<{ from: string | null; to: string | null }>({
    defaultValues: { from: daysAgo(7), to: daysAgo(0) },
  });
  const from = watch('from') ?? daysAgo(7);
  const to = watch('to') ?? from;
  const [branchId, setBranchId] = useState('');
  const [runBranch, setRunBranch] = useState(branches[0]?.branchId ?? '');
  const controls = useQuery({
    queryKey: accKeys.control(session?.tenantId, `${from}|${to}|${branchId}`),
    queryFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/accounting/daily-control', {
          params: { query: { from, to, branchId: branchId || undefined } },
        }),
      ),
  });
  const run = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/accounting/daily-control:run', {
          body: { branchId: runBranch || (branches[0]?.branchId ?? ''), date: to },
        }),
      ),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: accKeys.all(session?.tenantId) }),
  });
  const mismatches = num(controls.data?.mismatches);
  const columns: DataTableColumn<AccDailyControlView>[] = [
    { id: 'date', headerKey: 'acc:common.date', cell: (row) => <JalaliDate value={row.businessDate} /> },
    {
      id: 'scope',
      headerKey: 'acc:control.scope',
      cell: (row) => branchName(row.branchId) ?? t('acc:common.tenant'),
    },
    { id: 'check', headerKey: 'acc:control.check', cell: (row) => t(`acc:check.${row.check}`) },
    { id: 'expected', headerKey: 'acc:control.expected', cell: (row) => <Rial value={row.expected} /> },
    { id: 'actual', headerKey: 'acc:control.actual', cell: (row) => <Rial value={row.actual} /> },
    {
      id: 'difference',
      headerKey: 'acc:control.difference',
      cell: (row) =>
        num(row.difference) === 0 ? (
          '—'
        ) : (
          <strong>
            <Rial value={row.difference} testId="acc-control-difference" />
          </strong>
        ),
    },
    { id: 'status', headerKey: 'acc:common.status', cell: (row) => <ControlChip row={row} /> },
    { id: 'run', headerKey: 'acc:control.run_at', cell: (row) => <JalaliDate value={row.runAt} withTime /> },
  ];
  return (
    <ListPage
      screenId={SCR_STAFF_891}
      titleKey="acc:control.title"
      subtitleKey="acc:control.subtitle"
      filters={
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
          <JalaliDatePickerRHF control={control} name="from" labelKey="acc:common.from" />
          <JalaliDatePickerRHF control={control} name="to" labelKey="acc:common.to" />
          <TextField
            select
            size="small"
            label={t('acc:common.branch')}
            value={branchId}
            onChange={(event) => setBranchId(event.target.value)}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="">{t('acc:common.all')}</MenuItem>
            {branches.map((branch) => (
              <MenuItem key={branch.branchId} value={branch.branchId}>
                {branch.name}
              </MenuItem>
            ))}
          </TextField>
          {canRun ? (
            <>
              <TextField
                select
                size="small"
                label={t('acc:control.run_branch')}
                value={runBranch}
                onChange={(event) => setRunBranch(event.target.value)}
                sx={{ minWidth: 160 }}
              >
                {branches.map((branch) => (
                  <MenuItem key={branch.branchId} value={branch.branchId}>
                    {branch.name}
                  </MenuItem>
                ))}
              </TextField>
              <Button
                variant="outlined"
                onClick={() => run.mutate()}
                disabled={run.isPending || branches.length === 0}
                data-testid="acc-control-run"
              >
                {t('acc:control.run_for')}&nbsp;
                <JalaliDate value={to} />
              </Button>
            </>
          ) : null}
        </Stack>
      }
    >
      {/* «بدون اختلاف» فقط وقتی دست‌کم یک کنترل در بازه اجرا شده باشد (C-AGENT-1122). */}
      {controls.data && (controls.data.items?.length ?? 0) > 0 ? (
        <Alert
          severity={mismatches > 0 ? 'error' : 'success'}
          sx={{ mb: 2 }}
          data-testid="acc-control-summary"
          data-mismatches={mismatches}
        >
          {mismatches > 0
            ? t('acc:control.mismatches', { count: faDigits(mismatches) })
            : t('acc:control.ok')}
        </Alert>
      ) : null}
      {run.error ? <ErrorState error={run.error} compact /> : null}
      <DataTable
        captionKey="acc:control.title"
        columns={columns}
        rows={controls.data?.items}
        getRowId={(row) => row.id}
        isLoading={controls.isPending}
        error={controls.error}
        onRetry={() => void controls.refetch()}
        emptyTitleKey="acc:control.empty"
      />
    </ListPage>
  );
}
